/** 充值档位、预下单与支付结果查询。金额与到账数只展示服务端响应。 */

import { apiRequest } from "./api-client";
import type { ConversationMessage } from "@dreamverse/contracts";
import type { MiniappPaymentPayload, VirtualPaymentTicket } from "./host-bridge";

export interface TopupTier {
  key: string;
  priceCents: number;
  basePower: number;
  bonusPower: number;
  totalPower: number;
  grantedBond: number;
  grantedAffinity: number;
}

export interface TopupTiersResult {
  available: boolean;
  paymentMode: "development_stub" | "wechat_virtual";
  unavailableReason?: string;
  currency: "CNY";
  policyVersion: number | null;
  tiers: TopupTier[];
}

export interface TopupOrderResult {
  orderId: string;
  actionId: string;
  expiresAt: string;
  status: "paying" | "fulfilled";
  grantedPower: number;
  grantedBond: number;
  grantedAffinity: number;
  powerBalance: number | null;
  payment: MiniappPaymentPayload | null;
}

export type PaymentOrderStatus =
  | "created"
  | "paying"
  | "paid"
  | "fulfilled"
  | "closed"
  | "refunded";

export interface PaymentOrderResult {
  id: string;
  status: PaymentOrderStatus;
  tierKey: string | null;
  priceCents: number;
  currency: string;
  grantedPower: number | null;
  grantedBond: number;
  grantedAffinity: number;
  fulfilledAt: string | null;
  closedAt: string | null;
  expiresAt: string;
  powerBalance: number | null;
  resumePayment?: VirtualPaymentTicket | null;
}

export interface PaymentOrderListItem extends Omit<
  PaymentOrderResult,
  "powerBalance" | "grantedBond" | "grantedAffinity"
> {
  payChannel: "miniapp" | "jsapi" | "native" | "h5" | null;
  characterName: string | null;
  createdAt: string;
}

export interface PaymentOrderListResult {
  orders: PaymentOrderListItem[];
  total: number;
  hasMore: boolean;
}

export interface TopupThankResult {
  conversationId: string;
  /** 充值在对话时间线里的结构化系统事件。 */
  event: ConversationMessage | null;
  /** 已被用户删除的历史致谢重放时为 null；新充值正常都会返回刚生成的消息。 */
  message: ConversationMessage | null;
}

// React StrictMode 与路由切换可能让同一订单的 effect 重新订阅；浏览器内只发一条请求。
const activeTopupThankRequests = new Map<string, Promise<TopupThankResult>>();

export function fetchTopupTiers(signal?: AbortSignal): Promise<TopupTiersResult> {
  return apiRequest("/api/v1/app/payments/topup/tiers", { ...(signal ? { signal } : {}) });
}

export function createTopupOrder(tierKey: string, actionId: string): Promise<TopupOrderResult> {
  return apiRequest("/api/v1/app/payments/topup/orders", {
    body: { tierKey, actionId },
    timeoutMs: 15_000,
  });
}

export function fetchPaymentOrder(
  orderId: string,
  signal?: AbortSignal,
): Promise<PaymentOrderResult> {
  return apiRequest(`/api/v1/app/payments/orders/${encodeURIComponent(orderId)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function fetchPaymentOrders(
  signal?: AbortSignal,
  cursor = 0,
): Promise<PaymentOrderListResult> {
  const query = cursor > 0 ? `?cursor=${cursor}` : "";
  return apiRequest(`/api/v1/app/payments/orders${query}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * 到账后的独立角色致谢接口。接口只接订单 id；金额、灵力数与用户称呼均由服务端读取。
 */
export function requestTopupThank(orderId: string): Promise<TopupThankResult> {
  const existing = activeTopupThankRequests.get(orderId);
  if (existing) return existing;

  const request = apiRequest<TopupThankResult>(
    `/api/v1/app/payments/orders/${encodeURIComponent(orderId)}/thank`,
    { method: "POST", timeoutMs: 50_000 },
  );
  activeTopupThankRequests.set(orderId, request);
  const clear = () => {
    if (activeTopupThankRequests.get(orderId) === request) {
      activeTopupThankRequests.delete(orderId);
    }
  };
  request.then(clear, clear);
  return request;
}

export function newPaymentActionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `pay-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}
